import React, { useState, useEffect } from 'react';
import { FormField, Select, Input } from '../../../components/ui/Form';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { CURRENCIES, CurrencyCode } from '../../../config/currency';
import { useSettings } from '../../../hooks/useSettings';

export function GeneralSettings() {
  const { settings, loading, updateSettings } = useSettings();
  
  const [language, setLanguage] = useState('fr');
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY');
  const [timezone, setTimezone] = useState('Africa/Kinshasa');
  const [theme, setTheme] = useState('light');
  const [baseCurrency, setBaseCurrency] = useState<CurrencyCode>('CDF');
  const [displayCurrency, setDisplayCurrency] = useState<CurrencyCode>('CDF');
  const [exchangeRate, setExchangeRateValue] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  
  // Charger les paramètres depuis le hook
  useEffect(() => {
    if (settings?.general) {
      setLanguage(settings.general.language);
      setDateFormat(settings.general.dateFormat);
      setTimezone(settings.general.timezone);
      setTheme(settings.general.theme);
      setBaseCurrency(settings.general.baseCurrency as CurrencyCode);
      setDisplayCurrency(settings.general.displayCurrency as CurrencyCode);
    }
  }, [settings]);
  
  // Mettre à jour le taux de change affiché
  useEffect(() => {
    if (settings?.general?.exchangeRates) {
      const rate = settings.general.exchangeRates[displayCurrency];
      setExchangeRateValue(rate ? rate.toString() : '');
    }
  }, [displayCurrency, settings]);
  
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDisplayCurrency(e.target.value as CurrencyCode);
  };
  
  const handleExchangeRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExchangeRateValue(e.target.value);
  };
  
  const handleSavePreferences = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      const updatedExchangeRates = { ...settings?.general?.exchangeRates };
      if (displayCurrency !== baseCurrency && exchangeRate) {
        const rate = parseFloat(exchangeRate);
        if (!isNaN(rate) && rate > 0) {
          updatedExchangeRates[displayCurrency] = rate;
        }
      }
      
      await updateSettings({
        category: 'general',
        settings: {
          language,
          dateFormat,
          timezone,
          theme,
          baseCurrency,
          displayCurrency,
          exchangeRates: updatedExchangeRates
        }
      });
      
      alert('Préférences enregistrées avec succès');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde des préférences');
    } finally {
      setIsSaving(false);
    }
  };
  if (loading) {
    return (
      <div className="space-y-6">
        <Card title="Préférences générales">
          <p className="text-center py-4">Chargement des paramètres...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card title="Préférences générales">
        <form className="space-y-6" onSubmit={handleSavePreferences}>
          <div className="grid grid-cols-2 gap-6">
            <FormField label="Langue par défaut">
              <Select value={language} onChange={(e) => setLanguage(e.target.value)}>
                <option value="fr">Français</option>
                <option value="en">English</option>
              </Select>
            </FormField>

            <FormField label="Fuseau horaire">
              <Select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                <option value="Africa/Kinshasa">CAT (UTC+2)</option>
                <option value="UTC">UTC</option>
              </Select>
            </FormField>

            <FormField label="Format de date">
              <Select value={dateFormat} onChange={(e) => setDateFormat(e.target.value)}>
                <option value="DD/MM/YYYY">JJ/MM/AAAA</option>
                <option value="YYYY-MM-DD">AAAA-MM-JJ</option>
                <option value="MM/DD/YYYY">MM/JJ/AAAA</option>
              </Select>
            </FormField>

            <FormField label="Thème">
              <Select value={theme} onChange={(e) => setTheme(e.target.value)}>
                <option value="light">Clair</option>
                <option value="dark">Sombre</option>
                <option value="auto">Automatique</option>
              </Select>
            </FormField>

            <FormField label="Devise d'affichage">
              <Select
                value={displayCurrency}
                onChange={handleCurrencyChange}
              >
                {Object.values(CURRENCIES).map(currency => (
                  <option key={currency.code} value={currency.code}>
                    {currency.name} ({currency.symbol})
                  </option>
                ))}
              </Select>
            </FormField>
            
            {displayCurrency !== baseCurrency && CURRENCIES[baseCurrency] && CURRENCIES[displayCurrency] && (
              <FormField 
                label={`Taux de conversion (1 ${CURRENCIES[baseCurrency].symbol} = ? ${CURRENCIES[displayCurrency].symbol})`}
              >
                <div className="col-span-2">
                  <Input
                    type="number"
                    step="0.000001"
                    min="0.000001"
                    value={exchangeRate}
                    onChange={handleExchangeRateChange}
                    placeholder="Taux de conversion"
                    required
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Entrez le taux de conversion entre la devise de base et la devise sélectionnée
                  </p>
                </div>
              </FormField>
            )}
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Enregistrement...' : 'Enregistrer les préférences'}
            </Button>
          </div>
        </form>
      </Card>
      
      <Card title="Gestion des taux de conversion">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Devise de base: <strong>{CURRENCIES[baseCurrency]?.name || baseCurrency} ({CURRENCIES[baseCurrency]?.symbol || baseCurrency})</strong>
          </p>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Taux de conversion actuels</h3>
            <div className="space-y-2">
              {settings?.general?.exchangeRates && Object.entries(settings.general.exchangeRates)
                .filter(([code]) => code !== baseCurrency && CURRENCIES[code as CurrencyCode])
                .map(([code, rate]) => {
                  const currency = CURRENCIES[code as CurrencyCode];
                  const baseCurr = CURRENCIES[baseCurrency];
                  if (!currency || !baseCurr) return null;
                  
                  return (
                    <div key={code} className="flex justify-between">
                      <span>{currency.name}</span>
                      <span className="font-medium">
                        {rate ? `1 ${baseCurr.symbol} = ${rate} ${currency.symbol}` : 'Non configuré'}
                      </span>
                    </div>
                  );
                })
              }
            </div>
          </div>
          
          <div className="text-sm text-gray-600">
            <p>Les taux de conversion sont utilisés pour convertir les montants entre différentes devises lors de la génération des rapports financiers.</p>
            <p className="mt-1">Assurez-vous de mettre à jour régulièrement ces taux pour refléter les valeurs actuelles du marché.</p>
          </div>
        </div>
      </Card>
    </div>
  );
}